import React, { useCallback } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Typography,
  Button,
} from '@mui/material';
import {
  DragIndicator as DragIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from 'react-i18next';

function SortablePhoneRow({ item, onUpdate, onDelete, disabled }) {
  const { t } = useTranslation(['contacts']);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.temp_id,
    disabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box ref={setNodeRef} style={style} display="flex" gap={3} alignItems="center" mb={4}>
      <IconButton size="small" {...attributes} {...listeners} sx={{ cursor: 'grab', touchAction: 'none' }} disabled={disabled}>
        <DragIcon fontSize="small" />
      </IconButton>
      <TextField
        label={t('contacts:fields.phone_code')}
        value={item.country_code}
        onChange={(e) => onUpdate({ ...item, country_code: e.target.value })}
        sx={{ width: 120, flexShrink: 0 }}
        disabled={disabled}
      />
      <TextField
        label={t('contacts:fields.phone_number')}
        value={item.number}
        onChange={(e) => onUpdate({ ...item, number: e.target.value })}
        sx={{ flex: 1 }}
        disabled={disabled}
      />
      <TextField
        label={t('contacts:fields.entry_label')}
        value={item.label || ''}
        onChange={(e) => onUpdate({ ...item, label: e.target.value })}
        sx={{ width: 160, flexShrink: 0 }}
        placeholder={t('contacts:fields.entry_label_placeholder')}
        disabled={disabled}
      />
      <IconButton size="small" onClick={() => onDelete(item.temp_id)} disabled={disabled}>
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}

function SortableEmailRow({ item, onUpdate, onDelete, disabled }) {
  const { t } = useTranslation(['contacts']);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.temp_id,
    disabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box ref={setNodeRef} style={style} display="flex" gap={3} alignItems="center" mb={4}>
      <IconButton size="small" {...attributes} {...listeners} sx={{ cursor: 'grab', touchAction: 'none' }} disabled={disabled}>
        <DragIcon fontSize="small" />
      </IconButton>
      <TextField
        label={t('contacts:fields.email')}
        value={item.email}
        onChange={(e) => onUpdate({ ...item, email: e.target.value })}
        sx={{ flex: 1 }}
        disabled={disabled}
      />
      <TextField
        label={t('contacts:fields.entry_label')}
        value={item.label || ''}
        onChange={(e) => onUpdate({ ...item, label: e.target.value })}
        sx={{ width: 160, flexShrink: 0 }}
        placeholder={t('contacts:fields.entry_label_placeholder')}
        disabled={disabled}
      />
      <IconButton size="small" onClick={() => onDelete(item.temp_id)} disabled={disabled}>
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}

export function SortablePhoneList({ phones, onChange, disabled = false }) {
  const { t } = useTranslation(['contacts']);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = phones.findIndex((p) => p.temp_id === active.id);
      const newIndex = phones.findIndex((p) => p.temp_id === over.id);
      onChange(arrayMove(phones, oldIndex, newIndex));
    }
  }, [phones, onChange]);

  const handleAdd = useCallback(() => {
    onChange([...phones, { temp_id: `phone-${Date.now()}`, country_code: '+45', number: '', label: '' }]);
  }, [phones, onChange]);

  const handleUpdate = useCallback((updated) => {
    onChange(phones.map((p) => (p.temp_id === updated.temp_id ? updated : p)));
  }, [phones, onChange]);

  const handleDelete = useCallback((tempId) => {
    onChange(phones.filter((p) => p.temp_id !== tempId));
  }, [phones, onChange]);

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={5}>{t('contacts:fields.phone_numbers')}</Typography>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={phones.map((p) => p.temp_id)} strategy={verticalListSortingStrategy}>
          {phones.map((phone) => (
            <SortablePhoneRow
              key={phone.temp_id}
              item={phone}
              onUpdate={handleUpdate}
              onDelete={handleDelete}
              disabled={disabled}
            />
          ))}
        </SortableContext>
      </DndContext>
      <Button size="small" startIcon={<AddIcon />} onClick={handleAdd} disabled={disabled}>
        {t('contacts:actions.add_phone')}
      </Button>
    </Box>
  );
}

export function SortableEmailList({ emails, onChange, disabled = false }) {
  const { t } = useTranslation(['contacts']);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = emails.findIndex((e) => e.temp_id === active.id);
      const newIndex = emails.findIndex((e) => e.temp_id === over.id);
      onChange(arrayMove(emails, oldIndex, newIndex));
    }
  }, [emails, onChange]);

  const handleAdd = useCallback(() => {
    onChange([...emails, { temp_id: `email-${Date.now()}`, email: '', label: '' }]);
  }, [emails, onChange]);

  const handleUpdate = useCallback((updated) => {
    onChange(emails.map((e) => (e.temp_id === updated.temp_id ? updated : e)));
  }, [emails, onChange]);

  const handleDelete = useCallback((tempId) => {
    onChange(emails.filter((e) => e.temp_id !== tempId));
  }, [emails, onChange]);

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={5}>{t('contacts:fields.emails')}</Typography>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={emails.map((e) => e.temp_id)} strategy={verticalListSortingStrategy}>
          {emails.map((email) => (
            <SortableEmailRow
              key={email.temp_id}
              item={email}
              onUpdate={handleUpdate}
              onDelete={handleDelete}
              disabled={disabled}
            />
          ))}
        </SortableContext>
      </DndContext>
      <Button size="small" startIcon={<AddIcon />} onClick={handleAdd} disabled={disabled}>
        {t('contacts:actions.add_email')}
      </Button>
    </Box>
  );
}
